import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { Sparkles, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out successfully!');
    navigate('/login');
  };

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/games", label: "Games" },
    { to: "/numbers", label: "Numbers" },
    { to: "/lists", label: "Lists & More" },
    { to: "/drawings", label: "Drawings" },
    { to: "/web-tools", label: "Web Tools" },
    { to: "/statistics", label: "Statistics" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/randomness-verification", label: "Verification" },
    { to: "/testimonials", label: "Testimonials" },
    { to: "/learn-more", label: "About RNG" },
    { to: "/alternatives", label: "Alternatives" },
    { to: "/privacy-settings", label: "Privacy" },
  ];

  const adminNavItems = isAdmin ? [{ to: "/admin", label: "Admin Portal" }] : [];
  const allNavItems = [...navItems, ...adminNavItems];

  return (
    <nav className="sticky top-0 z-50 bg-card border-b border-border shadow-[var(--shadow-soft)]">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 text-xl font-bold text-primary">
            <Sparkles className="w-6 h-6 text-accent" />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              RandomGen
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {allNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className="px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-[var(--transition-smooth)]"
                activeClassName="text-primary bg-primary/10"
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <Button variant="outline" size="sm" className="ml-2" onClick={handleSignOut}>
                Sign Out
              </Button>
            ) : (
              <NavLink to="/login">
                <Button variant="default" size="sm" className="ml-2">
                  Login
                </Button>
              </NavLink>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 space-y-1">
            {allNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className="block px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
                activeClassName="text-primary bg-primary/10"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
              >
                Sign Out
              </Button>
            ) : (
              <NavLink to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="default" size="sm" className="w-full mt-2">
                  Login
                </Button>
              </NavLink>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
